// One andon. Chat first. Lifted from the Andon app's `AlertScreen`, mounted
// at `/alert/<id>` under the preview in place of the read-only sheet.
//
// TWO FACES, ONE SCREEN
//
//   before you walk over   an unowned andon, to somebody who could take it:
//                          what the operator said, what they answered, the
//                          collapsed rest, the thread, and the take button
//                          pinned where a thumb reaches. This is the brief,
//                          and it is most of the minutes this system saves.
//   the conversation       everything else. A status strip at the top, the
//                          thread taking the rest, and the state changes as
//                          things you SAY above the composer.
//
// WHY THE STATE CHANGES ARE QUICK REPLIES
//
// A chat-first screen has no action bar to hold "It has stopped now" — and it
// was the wrong shape anyway. Tapping it posts into the thread AND flips the
// state, so the escalation everybody sees has a visible cause rather than
// appearing from nowhere three minutes later.
//
// WHAT THE LIFT CHANGED. Who is reading comes from the shop floor's session
// — `connMode` for what the session may do, `isLeader` for what the server
// says about the account — rather than the Andon app's. The close is a sheet
// over the thread rather than a face of its own, which is how the frame draws
// it; the follow-up tasks and their sheet did not come across; the linked
// quality alert opens the shop floor's own QualitySheet. On a phone this
// replaces the frame, tab bar included; on a tablet it is a panel docked
// beside the screen that opened it. The URL is the sheet's — `useRouteSync`
// already writes `/alert/<id>` while the overlay is open.
//
// THE FACE IS THE FRAME'S (script L918, template L522-530). The header is
// the andon's kicker — "AN00114 · Steel Cutting · FL 1" — its TITLE, and the
// tag that says where it stands; under it the meta chips: how bad, who owns
// it or how long nobody has, who raised it and when, the pod, and "3rd time
// here · missing part" when this problem has come back. The Andon app's
// header carried the number and the type and left the title to the thread,
// which on a phone meant reading two screens to learn what the andon WAS.
// For a leader the header ends with "Go to <work centre>", the frame's one
// action on the sheet.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { andonApi, andonAvatarUrl } from '@/api/andon/client';
import type { AlertDetailVM } from '@/api/andon/types';
import { ApiError } from '@/api/http';
import { CancelSheet } from '@/components/andon/CancelSheet';
import { Composer } from '@/components/andon/Composer';
import { EtaSheet, type EtaIntent } from '@/components/andon/EtaSheet';
import { HandoverSheet } from '@/components/andon/HandoverSheet';
import { Ladder } from '@/components/andon/Ladder';
import { OwnerAvatar } from '@/components/andon/primitives';
import { AskedAnswers, ThreadView } from '@/components/andon/ThreadView';
import { Icon } from '@/components/ui/Icon';
import { SectionLabel } from '@/components/ui/misc';
import { Sheet } from '@/components/ui/Sheet';
import { useTick } from '@/hooks/useTick';
import { useT } from '@/i18n/useT';
import type { Translations } from '@/i18n/strings';
import { ACTION_LABEL, ackLabel, actionsFor, plusFifteen, type ActionKey } from '@/lib/actions';
import type { CancelReasonKey } from '@/lib/cancel';
import { floorClock } from '@/lib/floor';
import { gifSeed } from '@/lib/gifSeed';
import { STAR_COLOR, STAR_TINT, starLabel } from '@/lib/severity';
import { compact, timeOfDay } from '@/lib/time';
import { useAlert, useWorkcentres } from '@/queries';
import { useAlertDetail } from '@/queries/andon';
import { QualitySheet } from '@/screens/sheets/QualitySheet';
import { useStore } from '@/store';
import { CloseSheet } from './CloseSheet';

/**
 * The stop-estimate chips, in minutes. `null` is "not sure" and is deliberately
 * one of them rather than a way out of the question.
 */
const STOP_CHIPS = [20, 60, 120, null] as const;

export function AlertScreen({
  alertId,
  onClose,
  tablet,
  onGoToStation,
}: {
  alertId: number;
  onClose: () => void;
  tablet: boolean;
  /** The leader's "Go to <work centre>" at the end of the header (frame L918 `actions`). */
  onGoToStation?: (workcentreId: number, name: string) => void;
}) {
  const t = useT();
  const now = useTick();
  const qc = useQueryClient();
  const connMode = useStore((s) => s.connMode);
  const isLeader = useStore((s) => s.isLeader);
  const language = useStore((s) => s.language);
  const showToast = useStore((s) => s.showToast);
  const showError = useStore((s) => s.showError);
  const mode = connMode === 'user' ? ('user' as const) : connMode === 'kiosk' ? ('kiosk' as const) : ('none' as const);
  // The andon names its work centre and never carries the id; the shop
  // floor's own list has both, and the names come from the same rows.
  const workcentres = useWorkcentres();

  const [detailOpen, setDetailOpen] = useState(false);
  const [etaOpen, setEtaOpen] = useState(false);
  /**
   * Whether the take sheet was opened in order to hand the andon on.
   *
   * A handover passes on a response time, so it needs one to pass on — the
   * take sheet is the only door to it from an untaken andon, and this is which
   * of the two doors was used.
   */
  const [etaIntent, setEtaIntent] = useState<EtaIntent>('take');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelPreselect, setCancelPreselect] = useState<CancelReasonKey | null>(null);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [stopOpen, setStopOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [qaOpen, setQaOpen] = useState(false);
  /**
   * Opened straight onto the close, by whoever opened the andon with that verb
   * in mind. Read once and cleared, so reopening the same andon later lands
   * on the thread as usual.
   */
  const intent = useStore((s) => s.alertIntent);
  const clearAlertIntent = useStore((s) => s.clearAlertIntent);
  useEffect(() => {
    if (intent !== 'close') return;
    setCloseOpen(true);
    clearAlertIntent();
  }, [intent, clearAlertIntent]);

  const { data, error, fetchStatus } = useAlertDetail(alertId);
  const alert = data?.alert;
  // The linked quality alert, by the shop floor's own read, for its name on
  // the row and in the close's offer. Disabled until there is one to read.
  const qa = useAlert(alert?.qualityAlertId ?? null);

  // Every list this andon is on, this app's own included: the thread, the
  // Andon tab, the home rows, the leader's needs.
  const refresh = () => void qc.invalidateQueries({ queryKey: ['andons'] });
  const fail = (err: unknown) => showError(err instanceof ApiError ? err.message : t.alertDidNotGoThroughT);

  // TAKING IT. One call, where there were two — see `andonApi.respond`.
  const take = useMutation({
    mutationFn: (arg: { minutes: number | null; basis: 'chosen' | 'end_of_shift' }) => andonApi.respond(alertId, arg.minutes, arg.basis),
    // The TOAST reads the number off the row that came back, not the one that
    // was sent. On "after my shift" this client sent null and Odoo resolved
    // the minutes against the work centre's calendar, so echoing the request
    // would tell the responder they had promised nothing.
    onSuccess: (a) => {
      const first = a.raisedBy.split(' ')[0];
      showToast(a.etaMinutes == null ? `${t.toastTakenOnT} · ${first} ${t.toastToldT}` : `${t.toastTakenOnT} · ETA ${a.etaMinutes} ${t.minT} ${t.toastSentToT} ${first}`);
      setEtaOpen(false);
      setEtaIntent('take');
      refresh();
    },
    onError: fail,
  });
  const reviseFix = useMutation({
    mutationFn: (mins: number) => andonApi.reviseResolveEta(alertId, mins, null),
    onSuccess: (_a, mins) => {
      showToast(`${t.toastEstimateUpdatedT} ${fixEtaLabel(mins, t)}`);
      refresh();
    },
    onError: fail,
  });
  /**
   * Take it on the time just given, then open the responder list.
   *
   * The per-call `onSuccess` runs after the mutation's own, so the toast has
   * already said what was promised by the time the sheet opens: the sequence
   * on screen is the sequence on the record — a commitment, then the person it
   * passes to.
   */
  const takeThenHand = (minutes: number | null, basis: 'chosen' | 'end_of_shift') => take.mutate({ minutes, basis }, { onSuccess: () => setHandoverOpen(true) });
  const handOver = useMutation({
    mutationFn: (login: string) => andonApi.handOver(alertId, login),
    onSuccess: (a) => {
      showToast(`${a.owner.name} ${t.toastOwnsNowT}`);
      setHandoverOpen(false);
      refresh();
    },
    onError: fail,
  });
  const escalate = useMutation({
    mutationFn: () => andonApi.escalate(alertId),
    onSuccess: () => {
      showToast(t.toastEscalatedT);
      refresh();
    },
    onError: fail,
  });
  const stopped = useMutation({
    mutationFn: () => andonApi.raiseUrgency(alertId, 3),
    onSuccess: () => {
      showToast(t.toastEveryoneToldT);
      refresh();
    },
    onError: fail,
  });
  const estimate = useMutation({
    mutationFn: (minutes: 20 | 60 | 120 | null) => andonApi.estimateStop(alertId, minutes),
    onSuccess: () => {
      setStopOpen(false);
      refresh();
    },
    onError: fail,
  });

  const seed = useMemo(() => (alert ? gifSeed(alert, data?.thread ?? []) : 'reaction'), [alert, data?.thread]);

  // An andon that will not load is not a slow andon. Waiting forever on a word
  // that never changes leaves somebody with nothing to touch, on the screen
  // raising sent them to — so say which it is, and always leave a door.
  if (!alert) {
    return (
      <Frame tablet={tablet}>
        <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 24, textAlign: 'center' }}>
          <div style={{ maxWidth: 300 }}>
            <div style={{ font: '500 12.5px/1.6 Manrope,sans-serif', color: 'var(--sub)' }}>
              {error
                ? error instanceof ApiError
                  ? error.message
                  : t.alertCouldNotReadT
                : // A paused query is the dangerous one: no data, no error, and
                  // it stays that way for as long as the network is gone.
                  fetchStatus === 'paused'
                  ? t.alertNoConnectionT
                  : t.alertLoadingT}
            </div>
            <button
              type="button"
              className="press"
              onClick={onClose}
              style={{ marginTop: 16, height: 44, padding: '0 20px', borderRadius: 13, background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--ink)', font: "600 13px 'Space Grotesk',sans-serif" }}
            >
              {t.back}
            </button>
          </div>
        </div>
      </Frame>
    );
  }

  const sla = floorClock(alert, now, t.agoT);
  const actions = actionsFor(alert, { mode, leader: isLeader });
  const busy = take.isPending || reviseFix.isPending || handOver.isPending || escalate.isPending || stopped.isPending || estimate.isPending;
  const first = alert.raisedBy.split(' ')[0] ?? alert.raisedBy;
  const station = workcentres.data?.find((w) => w.name.trim() === alert.workcentre.trim()) ?? null;

  /**
   * The brief-first face: an unowned andon, to somebody who could take it.
   *
   * Ordered the way a responder needs it on the walk over — what the operator
   * said, then what they answered, then the rest behind a labelled row, then
   * the take button pinned where a thumb reaches.
   */
  const preWalk = alert.owner.unowned && alert.open && mode === 'user';
  const takeLabel = ackLabel(alert, t);

  /**
   * One tap, one thing.
   *
   * `ack` is a CHOICE rather than a single act — two minutes or fifteen — so
   * the row opens the sheet that asks. Every other action is unambiguous, and
   * the ones that need a number open the sheet that asks for it.
   */
  const run = (key: ActionKey) => {
    switch (key) {
      case 'ack':
        setEtaIntent('take');
        return setEtaOpen(true);
      case 'close':
        return setCloseOpen(true);
      case 'stopped':
        return stopped.mutate();
      case 'confirmStop':
      case 'estimateStop':
        return setStopOpen(true);
      case 'fixEta':
        return reviseFix.mutate(plusFifteen(alert.fixEta));
      case 'handover':
        return setHandoverOpen(true);
      case 'escalate':
        return escalate.mutate();
      case 'cancel':
        setCancelPreselect('sorted_myself');
        return setCancelOpen(true);
    }
  };

  const qaRow = alert.qualityAlertId != null && (
    <button
      type="button"
      className="press-row"
      onClick={() => setQaOpen(true)}
      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44, padding: '0 16px', textAlign: 'left', borderRadius: 14, background: 'var(--surface)', border: '1px solid var(--line)' }}
    >
      <Icon name="shield" size={15} strokeWidth={2} style={{ color: 'var(--warn)', flex: 'none' }} />
      <span style={{ flex: 1, font: '600 12.5px Manrope,sans-serif', color: 'var(--ink)' }}>
        {t.linkedQualityAlertT} {qa.data?.qa ?? ''}
      </span>
      <span style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)' }}>{qa.data?.stage ?? ''}</span>
      <Icon name="chevron" size={14} strokeWidth={2.4} style={{ color: 'var(--sub)' }} />
    </button>
  );

  /**
   * How long nobody has had it past the acknowledge target, in minutes.
   * The frame's `overBy`: drawn only on an unowned andon, and only once the
   * clock has run past the target.
   */
  const overBy = alert.owner.unowned && alert.open && alert.sla.dueAt != null && now > alert.sla.dueAt ? Math.floor((now - alert.sla.dueAt) / 60_000) : 0;
  const owner = alert.owner.name;
  const ownerShort = owner ? `${owner.split(' ')[0]} ${owner.split(' ').slice(-1)[0]?.[0] ?? ''}`.trim() : null;
  const metaChips: Array<{ text: string; tone: 'danger' | 'dangerSoft' | 'warn' | 'plain' }> = [
    {
      text: `${starLabel(alert.stars)} ${alert.stars === 3 ? t.metaStoppedT : alert.stars === 2 ? t.metaWillStopSoonT : t.metaCanWorkAroundT}`,
      tone: alert.stars === 3 ? 'dangerSoft' : 'plain',
    },
    ownerShort
      ? { text: `${ownerShort} ${t.ownsT}${alert.etaMinutes != null ? ` · ETA ${alert.etaMinutes} ${t.minT}` : ''}`, tone: 'plain' }
      : { text: `${t.nobodyYetT}${overBy > 0 ? ` · ${overBy} ${t.minOverT}` : ''}`, tone: alert.open ? 'danger' : 'plain' },
    { text: `${first} ${t.raisedT} · ${timeOfDay(alert.raisedAt)}`, tone: 'plain' },
    ...(alert.production ? [{ text: alert.production, tone: 'plain' as const }] : []),
    // Recurrence, the frame's "nth time here · type": only once this problem
    // has come back, because "1st time here" on every andon is noise.
    ...(alert.repeatCount > 0
      ? [{ text: `${t.nthTimeHereT.replace('{n}', ordinal(alert.repeatCount + 1, language))} · ${alert.typeLabel.toLowerCase()}`, tone: 'warn' as const }]
      : []),
  ];
  const tagUnowned = alert.owner.unowned && alert.open;

  return (
    <Frame tablet={tablet}>
      <header style={{ flex: 'none', padding: '14px 20px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
          <button
            type="button"
            className="press"
            aria-label={t.back}
            onClick={onClose}
            style={{ width: 40, height: 40, borderRadius: 13, background: 'var(--surface)', border: '1px solid var(--line)', display: 'grid', placeItems: 'center', color: 'var(--ink)', flex: 'none' }}
          >
            <Icon name="back" size={18} strokeWidth={1.8} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* The kicker: the identifier everybody quotes, the station, the
                line. Selectable, like the rest of what the andon itself says
                — see `.selectable` in tokens.css. */}
            <div className="selectable" style={{ font: "600 12px 'Space Grotesk',sans-serif", color: 'var(--sub)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {[alert.qa, alert.workcentre, alert.flowline?.replace(/^Flowline\s*/i, 'FL ')].filter(Boolean).join(' · ')}
            </div>
            <h1
              className="selectable"
              style={{ margin: '2px 0 0', font: "700 19px 'Space Grotesk',sans-serif", lineHeight: 1.25, color: 'var(--ink)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
            >
              {alert.title}
            </h1>
          </div>
          <span style={{ flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, marginTop: 4 }}>
            <span
              style={{
                padding: '3px 9px',
                borderRadius: 7,
                background: tagUnowned ? 'var(--danger)' : alert.open ? STAR_TINT[alert.stars] : 'var(--surface2)',
                color: tagUnowned ? '#fff' : alert.open ? STAR_COLOR[alert.stars] : 'var(--sub)',
                font: '700 10.5px Manrope,sans-serif',
                whiteSpace: 'nowrap',
              }}
            >
              {tagUnowned ? t.nobodyYetT : alert.stageLabel}
            </span>
            <span className="numeric" style={{ font: "700 14px 'Space Grotesk',sans-serif", color: sla.color }}>
              {sla.text}
            </span>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          {metaChips.map((c) => (
            <span
              key={c.text}
              className="selectable"
              style={{
                borderRadius: 999,
                padding: '4px 9px',
                font: '700 10.5px Manrope,sans-serif',
                background: c.tone === 'danger' ? 'var(--danger)' : c.tone === 'dangerSoft' ? 'rgba(255,90,95,.12)' : c.tone === 'warn' ? 'rgba(240,161,58,.14)' : 'var(--surface2)',
                color: c.tone === 'danger' ? '#fff' : c.tone === 'dangerSoft' ? 'var(--danger)' : c.tone === 'warn' ? 'var(--warn)' : 'var(--ink)',
              }}
            >
              {c.text}
            </span>
          ))}
        </div>
        {/* The frame's one action on the sheet, for a leader: walk over. */}
        {isLeader && station && onGoToStation && (
          <button
            type="button"
            className="press"
            onClick={() => {
              onClose();
              onGoToStation(station.id, station.name);
            }}
            style={{ marginTop: 10, width: '100%', minHeight: 40, borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--ink)', font: '700 12.5px Manrope,sans-serif' }}
          >
            {t.goToT} {station.name}
          </button>
        )}
      </header>

      {preWalk ? (
        <>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 20px 16px' }}>
            <section style={{ padding: '15px 16px', borderRadius: 18, background: 'var(--surface)', border: '1px solid var(--line)' }}>
              <SectionLabel style={{ marginBottom: 9 }}>
                {first} {t.alertSaidT}
              </SectionLabel>
              <p className="selectable" style={{ margin: 0, font: '500 13.5px/1.6 Manrope,sans-serif', color: 'var(--ink)' }}>
                {alert.words?.trim() || alert.title}
              </p>
            </section>

            {/* WHAT A PERSON ANSWERED, DIRECTLY UNDER WHAT THEY SAID. The intake
                answers are still the raiser's own bubble in the thread, but on
                this face they belong under their words: the responder reads
                what the operator said, then what they were asked, and decides
                whether to walk over. */}
            {alert.questions.asked.length > 0 && (
              <section style={{ marginTop: 12, padding: '15px 16px', borderRadius: 18, background: 'var(--surface)', border: '1px solid var(--line)' }}>
                <SectionLabel style={{ marginBottom: 9 }}>
                  {t.alertWhatT} {first} {t.alertWasAskedT}
                </SectionLabel>
                <span className="selectable" style={{ display: 'block' }}>
                  <AskedAnswers asked={alert.questions.asked} />
                </span>
              </section>
            )}

            {/* The rest of the status, behind a labelled row with a count. On
                a phone held while walking, every one of these open at once was
                a screen and a half for a decision that needs the top third. */}
            <div style={{ marginTop: 16, borderRadius: 16, background: 'var(--surface)', border: '1px solid var(--line)', overflow: 'hidden' }}>
              <DisclosureRow label={t.alertAlreadyNotifiedT} value={alert.notified.map((n) => n.label).join(' · ') || t.nobodyYetT} onClick={() => setDetailOpen(true)} />
            </div>
            {qaRow && <div style={{ marginTop: 10 }}>{qaRow}</div>}

            {/* THE THREAD IS HERE RATHER THAN BEHIND A ROW, and it is the other
                half of being able to message before anybody has acknowledged.
                A composer under a conversation you cannot see is a way to talk
                past somebody: two responders both asking the operator the same
                question, neither seeing the other ask it. */}
            <div style={{ marginTop: 16 }}>
              <SectionLabel style={{ marginBottom: 10 }}>{t.threadT}</SectionLabel>
              {data.thread.length || data.events.length ? (
                <div style={{ margin: '0 -20px' }}>
                  <ThreadView alertId={alertId} messages={data.thread} events={data.events} questions={alert.questions} raisedBy={alert.raisedBy} hideAsked={alert.questions.asked.length > 0} />
                </div>
              ) : (
                <p style={{ margin: 0, font: '500 12px/1.6 Manrope,sans-serif', color: 'var(--sub)' }}>
                  {t.alertNothingSaidAskT} {first} {t.alertSomethingBeforeWalkT}
                </p>
              )}
            </div>
          </div>

          <div style={{ flex: 'none', padding: '12px 20px calc(16px + env(safe-area-inset-bottom))', background: 'var(--surface)', borderTop: '1px solid var(--line)' }}>
            <SectionLabel style={{ marginBottom: 9, color: 'var(--ink)' }}>{t.alertNobodyHasThisT}</SectionLabel>
            {/* ONE BUTTON, AND THE ETA IS ASKED IN THE SHEET. Three buttons —
                2, 5, 15 — were one tap fewer and the whole reason the
                vocabulary could never say "after my shift". */}
            <button
              type="button"
              className="press-soft"
              disabled={busy}
              onClick={() => {
                setEtaIntent('take');
                setEtaOpen(true);
              }}
              style={{ width: '100%', minHeight: 54, borderRadius: 15, background: 'var(--pos)', color: '#fff', border: 0, marginBottom: 10, font: "700 15px 'Space Grotesk',sans-serif", opacity: busy ? 0.6 : 1, padding: '0 14px' }}
            >
              {takeLabel} — {t.alertSayWhenThereT}
            </button>
            {/* Labelled, and below the primary row. Handing over and escalating
                are different acts with opposite consequences, and as icon-only
                squares they were indistinguishable. */}
            <div style={{ display: 'flex', gap: 9 }}>
              {/* Through the take sheet, not straight to the responder list.
                  Handing on an andon nobody has taken changed the owner and
                  moved no clock; the sheet asks for that time first and opens
                  the list itself once it is given. */}
              <SecondaryAction
                icon="handover"
                label={t.verbHandToSomeoneT}
                disabled={busy}
                onClick={() => {
                  setEtaIntent('handover');
                  setEtaOpen(true);
                }}
              />
              <SecondaryAction icon="escalate" label={t.verbEscalateT} tone="danger" disabled={busy} onClick={() => escalate.mutate()} />
            </div>
          </div>

          {/* SAYING SOMETHING DOES NOT REQUIRE OWNING IT. The one window where
              a question is most useful — nobody has committed yet, and the
              operator is standing at the station with the answer — must not
              be the one window with no way to ask it. Below the take-it
              buttons, deliberately: taking it on is what this face is asking
              for; the composer is what to do INSTEAD. */}
          <Composer alertId={alertId} gifSeed={seed} />
        </>
      ) : (
        <>
          <StatusStrip alert={alert} now={now} onDetail={() => setDetailOpen(true)} />
          {qaRow && <div style={{ flex: 'none', margin: '0 20px 12px' }}>{qaRow}</div>}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <ThreadView alertId={alertId} messages={data.thread} events={data.events} questions={alert.questions} raisedBy={alert.raisedBy} />
          </div>
          <Composer
            alertId={alertId}
            gifSeed={seed}
            quickReplies={
              actions.length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, overflowX: 'auto', paddingBottom: 2 }}>
                  {actions.map((key) => (
                    <Quick
                      key={key}
                      // One chip rather than three for the take, and the honest
                      // verb for it: "Take over from Ben" when Ben has it.
                      label={key === 'ack' ? takeLabel : t[ACTION_LABEL[key].label]}
                      tone={ACTION_LABEL[key].tone}
                      disabled={busy}
                      onClick={() => run(key)}
                    />
                  ))}
                </div>
              )
            }
          />
        </>
      )}

      {/* ── the rest of it, behind DETAIL ────────────────────────────────── */}
      <Sheet open={detailOpen} onClose={() => setDetailOpen(false)} variant="tall" title={t.detailT}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <SectionLabel style={{ marginBottom: 9 }}>{t.alertWhoHasItT}</SectionLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <OwnerAvatar name={alert.owner.name} photoUrl={andonAvatarUrl(alert.owner)} />
              <div>
                <div style={{ font: '700 13.5px Manrope,sans-serif', color: alert.owner.unowned ? 'var(--danger)' : 'var(--ink)' }}>{alert.owner.name ?? t.nobodyYetT}</div>
                <div style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)', marginTop: 2 }}>
                  {alert.owner.unowned
                    ? `${alert.notified.length} ${t.alertNotifiedFirstOwnsT}`
                    : [alert.owner.role, alert.etaMinutes != null ? `ETA ${alert.etaMinutes} ${t.minT}` : null].filter(Boolean).join(' · ')}
                </div>
              </div>
              <span style={{ flex: 1 }} />
              <span style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)' }}>
                {alert.followers} {t.alertFollowingT}
              </span>
            </div>
          </div>

          {alert.stopForecast && (
            <div>
              <SectionLabel style={{ marginBottom: 9 }}>{t.alertTheStopT}</SectionLabel>
              <p style={{ margin: 0, font: '500 12.5px/1.6 Manrope,sans-serif', color: 'var(--ink)' }}>
                {alert.stopForecast.due
                  ? `${t.alertExpectedToStopAtT} ${timeOfDay(alert.stopForecast.predictedAt)} ${t.alertNobodyConfirmedT}`
                  : `${t.alertStopsInT} ${compact(alert.stopForecast.predictedAt - now)}, ${t.alertAtT} ${timeOfDay(alert.stopForecast.predictedAt)}.`}{' '}
                {alert.stopForecast.basis === 'estimated' ? t.alertOwnEstimateT : t.alertNotSureEndOfShiftT}
              </p>
            </div>
          )}

          {alert.notified.length > 0 && (
            <div>
              <SectionLabel style={{ marginBottom: 10 }}>{t.alertAlreadyNotifiedT}</SectionLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {alert.notified.map((notified, i) => (
                  <span key={`${notified.label}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 11px 6px 6px', borderRadius: 20, background: 'var(--surface2)' }}>
                    <OwnerAvatar name={notified.label} size={22} photoUrl={andonAvatarUrl(notified)} />
                    <span style={{ font: '600 11px Manrope,sans-serif', color: 'var(--ink)' }}>{notified.label}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <Ladder steps={alert.ladder} />

          {/* THE WAY OUT, at the bottom of the sheet rather than on the face.
              Cancelling is the raiser's or the owner's, and nobody else's: it
              is a claim about the problem, and only the person who called it
              in and the person who took it on are placed to make it. The
              server checks the same rule under a row lock. Nothing is
              deleted either way, which the sheet says. */}
          {alert.open && (alert.raisedByMe || alert.mine) && (
            <div style={{ borderTop: '1px solid var(--line)', paddingTop: 16 }}>
              <button
                type="button"
                className="press-soft"
                onClick={() => {
                  setDetailOpen(false);
                  setCancelPreselect(null);
                  setCancelOpen(true);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 48, padding: '0 14px', borderRadius: 14, background: 'var(--surface)', border: '1px solid var(--line)', color: 'var(--danger)', font: '700 13px Manrope,sans-serif', cursor: 'pointer' }}
              >
                <Icon name="cross" size={16} strokeWidth={2.4} />
                {t.alertCancelThisT}
                <span style={{ flex: 1 }} />
                <span style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)' }}>{t.alertNeedsReasonT}</span>
              </button>
            </div>
          )}
        </div>
      </Sheet>

      {/* WHEN WILL IT STOP, AND THEN: DID IT? The prompt asks rather than
          acting. When the predicted moment arrives this does not upgrade to
          three stars on a timer, because three stars means the line IS
          stopped: it broadcasts, and it stamps the start of the downtime this
          system reports. Writing that from an operator's earlier guess
          manufactures the fact. */}
      <Sheet open={stopOpen} onClose={() => setStopOpen(false)} title={alert.stopForecast?.due ? t.verbDidItStopT : t.verbWhenWillItStopT}>
        {alert.stopForecast?.due ? (
          <>
            <p style={{ margin: '0 0 14px', font: '500 12.5px/1.6 Manrope,sans-serif', color: 'var(--sub)' }}>
              {t.stopYouSaidByT} {timeOfDay(alert.stopForecast.predictedAt)}. {alert.stopForecast.lapsed ? t.stopLapsedT : t.stopNobodyElseSeesT}
            </p>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <button
                type="button"
                className="press-soft"
                disabled={busy}
                onClick={() => {
                  setStopOpen(false);
                  stopped.mutate();
                }}
                style={{ flex: 1, minHeight: 50, borderRadius: 14, background: STAR_COLOR[3], color: '#fff', font: "700 14px 'Space Grotesk',sans-serif" }}
              >
                {t.stopItHasStoppedT}
              </button>
            </div>
            <p style={{ margin: '0 0 10px', font: '600 12px Manrope,sans-serif', color: 'var(--sub)' }}>{t.stopStillRunningT}</p>
          </>
        ) : (
          <p style={{ margin: '0 0 14px', font: '500 12.5px/1.6 Manrope,sans-serif', color: 'var(--sub)' }}>{t.stopDecidesWhoT}</p>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
          {STOP_CHIPS.map((minutes) => (
            <button
              key={String(minutes)}
              type="button"
              className="press"
              disabled={busy}
              onClick={() => estimate.mutate(minutes)}
              style={{ flex: '1 1 auto', minWidth: 78, minHeight: 48, borderRadius: 14, background: 'var(--surface2)', border: '1px solid var(--line)', color: 'var(--ink)', font: "700 13.5px 'Space Grotesk',sans-serif" }}
            >
              {minutes == null ? t.qNotSureT : minutes < 60 ? `${minutes} ${t.minT}` : `${minutes / 60} ${minutes === 60 ? t.hrT : t.hrsT}`}
            </button>
          ))}
        </div>
      </Sheet>

      <HandoverSheet open={handoverOpen} onClose={() => setHandoverOpen(false)} onPick={(login) => handOver.mutate(login)} pending={handOver.isPending} />

      <EtaSheet
        alert={alert}
        open={etaOpen}
        intent={etaIntent}
        onClose={() => {
          setEtaOpen(false);
          setEtaIntent('take');
        }}
        busy={take.isPending}
        onSubmit={(minutes, basis) => take.mutate({ minutes, basis })}
        // Straight from one sheet to the other, THROUGH the take: the andon
        // goes across with a time on it and the operator's clock stopped,
        // which is the whole difference between passing on a job and passing
        // on a wait.
        onHandover={takeThenHand}
      />

      <CancelSheet alert={alert} open={cancelOpen} preselect={cancelPreselect} onClose={() => setCancelOpen(false)} onCancelled={onClose} />

      <CloseSheet alert={alert} open={closeOpen} onClose={() => setCloseOpen(false)} onDone={onClose} qaName={qa.data?.qa ?? null} />

      {/* The linked quality alert, in the shop floor's own sheet. Mounted here
          rather than through the shell's overlay, because the shell holds one
          overlay at a time and this one is open already. */}
      {alert.qualityAlertId != null && (
        <QualitySheet
          open={qaOpen}
          alertId={alert.qualityAlertId}
          onClose={() => setQaOpen(false)}
          // An alert reached from a thread is already paired with this
          // andon, so the sheet never offers to raise another; the verb is
          // the shell's, for the sheet opened from the Quality tab.
          onRaiseAndon={() => undefined}
        />
      )}
    </Frame>
  );
}

function fixEtaLabel(m: number, t: Translations): string {
  return m < 60 ? `${m} ${t.minT}` : `${m / 60} ${m === 60 ? t.hrT : t.hrsT}`;
}

/**
 * "2nd", "3rd", "11th" — in English; the other two languages count with a
 * plain number inside their own phrase (see `nthTimeHereT`).
 */
function ordinal(n: number, language: string): string {
  if (language !== 'AU') return String(n);
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  const suffix = n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
}

/**
 * The andon's own frame.
 *
 * It fills the surface it is on: the whole screen on a phone (the tab bar
 * hides here, which is where the thread's extra height comes from), and a
 * docked panel BESIDE the screen on a tablet — because working an andon
 * without losing sight of the floor is the point of the width.
 *
 * A real flex sibling, never an overlay with a backdrop: a transparent sheet
 * over the floor made every tap on the next card a tap that closed the panel.
 * Escape and the back button both close, and neither was ever the thing
 * anybody reached for mid-queue.
 */
function Frame({ tablet, children }: { tablet: boolean; children: React.ReactNode }) {
  if (!tablet) {
    return <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, background: 'var(--bg)', animation: 'ipFade .2s ease' }}>{children}</div>;
  }
  return (
    <aside
      role="region"
      aria-label="Andon"
      style={{
        flex: 'none',
        width: 'min(560px, 46vw)',
        minWidth: 380,
        height: '100%',
        background: 'var(--bg)',
        borderLeft: '1px solid var(--line)',
        boxShadow: 'var(--elev)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        animation: 'ipSlideIn .22s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      {children}
    </aside>
  );
}

/**
 * Everything about who is coming, in one strip.
 *
 * Six stacked cards became this: who has it, when it stops, who is following,
 * when it escalates. DETAIL opens the rest. That is where the thread's height
 * comes from, and none of what moved was something anybody acted on from the
 * top of this screen.
 */
function StatusStrip({ alert, now, onDetail }: { alert: AlertDetailVM; now: number; onDetail: () => void }) {
  const t = useT();
  const acked = alert.acked;
  const tone = acked ? 'var(--pos)' : alert.owner.unowned ? 'var(--danger)' : STAR_COLOR[alert.stars];

  const headline = !alert.open
    ? `${t.alertSettledT} · ${alert.stageLabel.toLowerCase()}`
    : acked
      ? `${(alert.acknowledgedByName ?? alert.owner.name ?? t.alertSomebodyT).split(' ')[0]} ${alert.responding ? t.alertIsOnItT : t.alertIsComingT}${
          !alert.responding && alert.etaMinutes != null ? ` · ${alert.etaMinutes} ${t.minT}` : ''
        }`
      : /**
         * OWNED AND UNACKNOWLEDGED IS A REAL STATE. Handing an andon over sets
         * the owner and touches nothing else — no clock, no acknowledgement —
         * because being given an andon is not the same as promising to be
         * somewhere. So the name goes in and the waiting stays in; the border
         * is still the star colour rather than green.
         */
        alert.owner.unowned || alert.owner.name == null
        ? t.alertNobodyTakenYetT
        : `${t.alertHandedToT} ${alert.owner.name.split(' ')[0]} · ${t.alertStillNoEtaT}`;

  const detail = [
    alert.stopForecast && !alert.stopForecast.due ? `${t.alertStopsByT} ${timeOfDay(alert.stopForecast.predictedAt)}` : null,
    alert.responding && alert.fixEta
      ? alert.fixEta.overdue
        ? `${t.alertRunningOverByT} ${compact(-alert.fixEta.msLeft)}`
        : `${t.alertFixInT} ${compact(alert.fixEta.msLeft)}`
      : null,
    `${alert.followers} ${t.alertFollowingT}`,
    alert.open && !acked && alert.sla.dueAt != null ? `${t.alertEscalatesT} ${compact(Math.max(0, alert.sla.dueAt - now))}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <button
      type="button"
      className="press-soft"
      onClick={onDetail}
      style={{
        flex: 'none',
        margin: '0 20px 12px',
        padding: '13px 15px',
        borderRadius: 17,
        background: acked ? 'rgba(55,199,126,.1)' : 'var(--surface)',
        border: `1.5px solid ${tone}`,
        display: 'flex',
        alignItems: 'center',
        gap: 13,
        textAlign: 'left',
        width: 'calc(100% - 40px)',
      }}
    >
      <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 11, background: tone, color: '#fff', display: 'grid', placeItems: 'center', flex: 'none' }}>
        <Icon name={acked ? 'check' : alert.owner.unowned ? 'warn' : 'clock'} size={19} strokeWidth={acked ? 3 : 1.9} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', font: "700 15px 'Space Grotesk',sans-serif", color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{headline}</span>
        <span style={{ display: 'block', font: '500 11px Manrope,sans-serif', color: 'var(--sub)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail}</span>
      </span>
      <span style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 5, color: 'var(--sub)' }}>
        <span style={{ font: "700 9px 'Space Grotesk',sans-serif", letterSpacing: '.08em', textTransform: 'uppercase' }}>{t.detailT}</span>
        <Icon name="caret" size={13} strokeWidth={1.8} />
      </span>
    </button>
  );
}

function DisclosureRow({ label, value, onClick }: { label: string; value: string; onClick: () => void }) {
  return (
    <button type="button" className="press-row" onClick={onClick} style={{ width: '100%', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left' }}>
      <span style={{ flex: 1, font: '600 12.5px Manrope,sans-serif', color: 'var(--ink)' }}>{label}</span>
      <span style={{ font: '500 11.5px Manrope,sans-serif', color: 'var(--sub)', maxWidth: '50%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
      <Icon name="chevron" size={14} strokeWidth={2.4} style={{ color: 'var(--sub)' }} />
    </button>
  );
}

function SecondaryAction({ icon, label, tone, disabled, onClick }: { icon: 'handover' | 'escalate'; label: string; tone?: 'danger'; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className="press"
      disabled={disabled}
      onClick={onClick}
      style={{ flex: 1, minHeight: 40, borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--line)', color: tone === 'danger' ? 'var(--danger)' : 'var(--sub)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, font: '600 12px Manrope,sans-serif', opacity: disabled ? 0.55 : 1, padding: '0 10px' }}
    >
      <Icon name={icon} size={15} strokeWidth={1.6} />
      {label}
    </button>
  );
}

function Quick({ label, tone, disabled, onClick }: { label: string; tone: 'danger' | 'pos' | 'quiet'; disabled?: boolean; onClick: () => void }) {
  const colour = tone === 'danger' ? { border: 'var(--danger)', color: 'var(--danger)' } : tone === 'pos' ? { border: 'var(--pos)', color: 'var(--pos)' } : { border: 'var(--line)', color: 'var(--sub)' };
  return (
    <button
      type="button"
      className="press"
      disabled={disabled}
      onClick={onClick}
      style={{
        flex: 'none',
        height: 40,
        padding: '0 14px',
        borderRadius: 13,
        background: 'var(--surface)',
        border: `${tone === 'quiet' ? 1 : 1.5}px solid ${colour.border}`,
        color: colour.color,
        font: `${tone === 'quiet' ? 600 : 700} 12.5px ${tone === 'quiet' ? 'Manrope' : "'Space Grotesk'"},sans-serif`,
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {label}
    </button>
  );
}
